import type { FC } from "react";
import FlatFlipGraphic from "./connectorFamilyGraphics/FlatFlip";
import GapGraphic from "./connectorFamilyGraphics/Gap";
import type { MillimetersToPixels } from "./connectorFamilyGraphics/KkScrew";
import SnapGraphic from "./connectorFamilyGraphics/Snap";
import TerralockGraphic from "./connectorFamilyGraphics/Terralock";
import TvmGraphic from "./connectorFamilyGraphics/Tvm";
import styles from "./FinishedDeckSectionConnectors.module.css";
import type { GapPatternSegment, SectionConnectorsLayout } from "./finishedDeckSectionGeometry";

type FinishedDeckSectionConnectorsProps = {
  connectors: SectionConnectorsLayout;
  gapSegments: GapPatternSegment[];
  toPixels: MillimetersToPixels;
};

// One connector in each gap between the section's boards, drawn with its family's own graphic.
const FinishedDeckSectionConnectors: FC<FinishedDeckSectionConnectorsProps> = ({
  connectors,
  gapSegments,
  toPixels,
}) => {
  const {
    baseHeight,
    boardScrew,
    graphic: ConnectorGraphic,
    graphicHeight,
    graphicWidth,
    graphicY,
    kind,
    snapScrew,
    substructureScrew,
    terralock,
    tvmTotalHeightMm,
  } = connectors;

  const renderConnector = (gapSegment: GapPatternSegment) => {
    if (!ConnectorGraphic) {
      return null;
    }

    const x = gapSegment.centerX - graphicWidth / 2;

    switch (kind) {
      case "terralock":
        return (
          <TerralockGraphic
            x={gapSegment.rightX - terralock.postSize - terralock.postLeftOffset}
            y={terralock.y}
            width={terralock.width}
            height={terralock.height}
            topHeight={terralock.topHeight}
            baseHeight={baseHeight}
            postLeftOffset={terralock.postLeftOffset}
            postSize={terralock.postSize}
            boardScrew={boardScrew}
            substructureScrew={substructureScrew}
            screwVisibleLength={terralock.screwVisibleLengthMm}
            toPixels={toPixels}
          />
        );
      case "flatFlip":
        return (
          <FlatFlipGraphic
            className={styles.connectorGraphic}
            x={x}
            y={graphicY}
            width={graphicWidth}
            height={graphicHeight}
            preserveAspectRatio="xMidYMid meet"
            screw={substructureScrew}
          />
        );
      case "gap":
        return (
          <GapGraphic
            className={styles.connectorGraphic}
            x={x}
            y={graphicY}
            width={graphicWidth}
            height={graphicHeight}
            preserveAspectRatio="xMidYMid meet"
            screw={substructureScrew}
          />
        );
      case "tvm":
        return (
          <TvmGraphic
            className={styles.connectorGraphic}
            x={x}
            y={graphicY}
            width={graphicWidth}
            height={graphicHeight}
            totalHeight={tvmTotalHeightMm}
            preserveAspectRatio="xMidYMid meet"
            screw={substructureScrew}
          />
        );
      case "snap":
        return (
          <SnapGraphic
            className={styles.connectorGraphic}
            x={x}
            y={graphicY}
            width={graphicWidth}
            height={graphicHeight}
            preserveAspectRatio="xMidYMid meet"
            screw={snapScrew}
          />
        );
      default:
        return (
          <ConnectorGraphic
            className={styles.connectorGraphic}
            x={x}
            y={graphicY}
            width={graphicWidth}
            height={graphicHeight}
            preserveAspectRatio="xMidYMid meet"
          />
        );
    }
  };

  return gapSegments.map((gapSegment) => <g key={`${gapSegment.key}-connector`}>{renderConnector(gapSegment)}</g>);
};

export default FinishedDeckSectionConnectors;
